import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Loader2,
  RefreshCw,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchEntityMappings, fetchFieldSyncLogs } from "@/lib/api";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  Row,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import type { FieldMappingSyncLog, SyncLogsResponse } from "../../shared/api-types";

// interface FieldSyncLogsApiResponse {
//   logs: FieldMappingSyncLog[];
//   statusCounts?: {
//     done: number;
//     error: number;
//     no_update_needed: number;
//     pending: number;
//     total: number;
//   };
// }

// Define the complete list of available columns including all possible FieldMappingSyncLog properties
const ALL_COLUMNS = [
  { key: "entityName", label: "Entity Name", priority: 1 },
  { key: "entityId", label: "Entity ID", priority: 1 },
  { key: "status", label: "Status", priority: 1 },
  { key: "batchId", label: "Batch ID", priority: 2 },
  { key: "timestamp", label: "Timestamp", priority: 2 },
  { key: "updatedFields", label: "Updated Fields", priority: 3 },
  { key: "processedAt", label: "Processed At", priority: 3 },
  { key: "mappingId", label: "Mapping ID", priority: 3 },
  { key: "errorMessage", label: "Error Message", priority: 3 },
  { key: "correlationId", label: "Correlation ID", priority: 4 },
  { key: "relationName", label: "Relation Name", priority: 4 },
  { key: "message", label: "Message", priority: 3 },
  { key: "error", label: "Error", priority: 3 },
  { key: "syncLogs", label: "Logs", priority: 3 },
];

const FieldSyncLogs = () => {
  const [parentEntity, setParentEntity] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [batchId, setBatchId] = useState<string>("all");
  const [timePeriod, setTimePeriod] = useState<string>("all");
  const [limit, setLimit] = useState<number>(25);
  // Remove offset-based pageIndex, add cursor stack for previous/next
  const [cursorStack, setCursorStack] = useState<Array<{ lastSeenTimestamp: string | null, lastSeenId: string | null }>>([{ lastSeenTimestamp: null, lastSeenId: null }]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(0);
  const [selectedLog, setSelectedLog] = useState<FieldMappingSyncLog | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // State for server-side search term
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Add state for selected columns
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    // Try to load from localStorage, else default
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("fieldMappingLogColumns");
      if (stored) return JSON.parse(stored);
    }
    // Default to high priority columns (priority 1)
    return ALL_COLUMNS.filter(col => col.priority === 1).map(col => col.key);
  });

  // Add state for column display mode
  const [columnDisplayMode, setColumnDisplayMode] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("fieldMappingLogColumnMode");
      if (stored) return stored;
    }
    return "priority"; // Default to priority mode
  });

  // Persist selection in localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("fieldMappingLogColumns", JSON.stringify(selectedColumns));
      localStorage.setItem("fieldMappingLogColumnMode", columnDisplayMode);
    }
  }, [selectedColumns, columnDisplayMode]);

  // For table sorting
  const [sorting, setSorting] = useState<SortingState>([
    { id: "timestamp", desc: true },
  ]);

  // For table filtering
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Query to get entity mappings for dropdown options
  const { data: entityMappings, isLoading: mappingsLoading } = useQuery({
    queryKey: ["/api/entity-mappings"],
    queryFn: () => fetchEntityMappings(),
  });

  // Compute current cursor
  const currentCursor = cursorStack[currentCursorIndex] || { lastSeenTimestamp: null, lastSeenId: null };

  // Query to get sync logs based on selected filters and cursor-based pagination
  const {
    data: syncLogsData,
    isLoading: syncLogsLoading,
    isRefetching,
    refetch,
  } = useQuery<SyncLogsResponse>({
    queryKey: [
      "/api/query-field-mapping-logs",
      { parentEntity, status, batchId, timePeriod, searchTerm, limit, lastSeenTimestamp: currentCursor.lastSeenTimestamp, lastSeenId: currentCursor.lastSeenId },
    ],
    queryFn: async () => {
      // Calculate start time based on time period
      const startTime = timePeriod !== "all" ? getStartTimeFromPeriod(timePeriod) : undefined;

      // Add current time as endTime when startTime is defined
      const endTime = startTime ? new Date().toISOString() : undefined;
      
      // Log query parameters for debugging
      console.log("Filter values:", { 
        parentEntity, 
        status, 
        batchId, 
        timePeriod, 
        startTime, 
        endTime,
        limit, 
        cursor: currentCursor 
      });
      
      // Call fetchFieldSyncLogs with individual parameters including the new filters
      const response = await fetchFieldSyncLogs(
        parentEntity !== "all" ? parentEntity : undefined,
        status !== "all" ? status : undefined,
        batchId !== "all" ? batchId : undefined,
        startTime,
        endTime, // endTime (current time)
        currentCursor.lastSeenTimestamp || undefined,
        currentCursor.lastSeenId || undefined,
        searchTerm || undefined,
        limit
      ) as SyncLogsResponse;
      
      // Log the response for debugging
      console.log("Field sync logs response:", { 
        logCount: response.logs?.length, 
        totalCount: response.totalCount,
        statusCounts: response.statusCounts 
      });
      
      return response;
    },
    staleTime: 10000,
  });

  // Invalidate and refetch when filters change
  useEffect(() => {
    refetch();
  }, [status, parentEntity, batchId, timePeriod, searchTerm]);

  // Get unique parent entities from mappings
  const uniqueParentEntities = entityMappings
    ? Array.from(new Set(entityMappings.map((m) => m.parentEntity)))
    : [];

  // Get unique batch IDs from logs for dropdown
  const uniqueBatchIds = useMemo(() => {
    if (!syncLogsData?.logs) return [];
    return Array.from(new Set(
      syncLogsData.logs
        .filter(log => log.batchId)
        .map(log => log.batchId as string)
    )).sort();
  }, [syncLogsData?.logs]);

  // Helper function to convert time period to date string
  const getStartTimeFromPeriod = (period: string): string | undefined => {
    if (period === "all") return undefined;
    
    // Make sure we have a valid period format
    if (!(period.endsWith('h') || period.endsWith('d'))) {
      console.error(`Invalid time period format: ${period}. Expected format like "24h" or "7d"`);
      return undefined;
    }
    
    try {
      const now = new Date();
      const startDate = new Date(now);
      
      // Check if the period is in hours or days
      if (period.endsWith('h')) {
        const hours = parseInt(period.replace('h', ''));
        if (isNaN(hours)) {
          console.error(`Invalid hour value: ${period}`);
          return undefined;
        }
        startDate.setHours(now.getHours() - hours);
      } else if (period.endsWith('d')) {
        const days = parseInt(period.replace('d', ''));
        if (isNaN(days)) {
          console.error(`Invalid day value: ${period}`);
          return undefined;
        }
        startDate.setDate(now.getDate() - days);
      }
      
      console.log(`Time period ${period} converted to: ${startDate.toISOString()}`);
      return startDate.toISOString();
    } catch (error) {
      console.error("Error converting time period:", error);
      return undefined;
    }
  };

  // Use totalCount for pagination
  const totalCount = syncLogsData?.totalCount || 0;

  // Build columns dynamically based on display mode
  const getActiveColumns = (): string[] => {
    if (columnDisplayMode === "custom") {
      return selectedColumns;
    } else {
      // Priority mode - show priority 1 and 2 columns
      return ALL_COLUMNS.filter(col => col.priority <= 2).map(col => col.key);
    }
  };

  // Build columns dynamically
  const columns: ColumnDef<FieldMappingSyncLog>[] = [
    ...ALL_COLUMNS.filter(col => getActiveColumns().includes(col.key)).map(col => {
      // Custom cell renderers for some columns
      if (col.key === "partitionKey") {
        return {
          accessorKey: "partitionKey",
          header: "Partition Key",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const partitionKey = row.original.partitionKey;
            return (
              <div className="flex items-center space-x-2">
                <span className="font-medium">{partitionKey}</span>
              </div>
            );
          },
        };
      }
      if (col.key === "status") {
        return {
          accessorKey: "status",
          header: "Status",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const status = row.original.status;
            let color = "bg-gray-200 text-gray-800";
            if (status === "done") color = "bg-green-100 text-green-800";
            else if (status === "error") color = "bg-red-100 text-red-800";
            else if (status === "pending") color = "bg-blue-100 text-blue-800";
            else if (status === "no_update_needed") color = "bg-yellow-100 text-yellow-800";
            return <Badge className={color}>{status}</Badge>;
          },
        };
      }
      if (col.key === "timestamp") {
        return {
          accessorKey: "timestamp",
          header: "Timestamp",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const timestamp = row.original.timestamp || row.original.processedAt;
            return (
              <div className="text-sm">
                {timestamp instanceof Date
                  ? format(timestamp, "yyyy-MM-dd HH:mm:ss")
                  : timestamp
                    ? format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss")
                    : "N/A"}
              </div>
            );
          },
        };
      }
      if (col.key === "syncLogs") {
        return {
          accessorKey: "syncLogs",
          header: "Logs",
          cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
            const logs = row.getValue("syncLogs");
            const logText = typeof logs === 'object' && logs !== null
              ? JSON.stringify(logs)
              : logs as string;
            return (
              <div className="max-w-[400px] overflow-hidden">
                <div className="line-clamp-2 text-sm whitespace-pre-wrap break-words">
                  {logText}
                </div>
              </div>
            );
          },
        };
      }
      // Default: generic cell
      return {
        accessorKey: col.key,
        header: col.label,
        cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
          const value = row.original[col.key as keyof FieldMappingSyncLog];
          if (value === undefined || value === null) return <span className="text-gray-400">N/A</span>;
          if (typeof value === "boolean") return value ? "Yes" : "No";
          if (typeof value === "object") return <span className="text-xs">{JSON.stringify(value)}</span>;
          return <span>{String(value)}</span>;
        },
      };
    }),
    // Always include actions column
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: { row: Row<FieldMappingSyncLog> }) => {
        return (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedLog(row.original);
                setIsDetailsOpen(true);
              }}
            >
              View Details
            </Button>
          </div>
        );
      },
    },
  ];

  // Update table instance for server-side pagination
  const table = useReactTable<FieldMappingSyncLog>({
    data: syncLogsData?.logs || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: currentCursorIndex,
        pageSize: limit,
      },
    },
    manualPagination: true,
    pageCount: Math.ceil(totalCount / limit),
  });

  const handleSearch = () => {
    if (searchInputRef.current) {
      const term = searchInputRef.current.value.trim();
      
      // Only update if the term has changed
      if (term !== searchTerm) {
        console.log(`Searching for: "${term}" (server-side search across all fields)`);
        setSearchTerm(term);
        
        // Reset cursor stack when changing search term - effect will handle refetch
        setCursorStack([{ lastSeenTimestamp: null, lastSeenId: null }]);
        setCurrentCursorIndex(0);
      }
    }
  };

  const clearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    
    // Only update if there was a search term
    if (searchTerm) {
      console.log("Clearing search term");
      setSearchTerm("");
      
      // Reset cursor stack when clearing search term - effect will handle refetch
      setCursorStack([{ lastSeenTimestamp: null, lastSeenId: null }]);
      setCurrentCursorIndex(0);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const clearFilters = () => {
    setParentEntity("all");
    setStatus("all");
    setBatchId("all");
    setTimePeriod("all");
  };

  // Create status filter options
  const statusOptions = [
    { value: "done", label: "Done" },
    { value: "error", label: "Error" },
    { value: "pending", label: "Pending" },
    { value: "no_update_needed", label: "No Updates" },
  ];

  // Create time period options
  const timePeriodOptions = [
    { value: "all", label: "All Time" },
    { value: "1h", label: "Last Hour" },
    { value: "4h", label: "Last 4 Hours" },
    { value: "1d", label: "Last 24 Hours" },
    { value: "2d", label: "Last 2 Days" },
    { value: "3d", label: "Last 3 Days" },
    { value: "4d", label: "Last 4 Days" },
    { value: "5d", label: "Last 5 Days" },
    { value: "6d", label: "Last 6 Days" },
    { value: "7d", label: "Last 7 Days" },
  ];

  // When filters or limit change, reset cursor stack
  useEffect(() => {
    setCursorStack([{ lastSeenTimestamp: null, lastSeenId: null }]);
    setCurrentCursorIndex(0);
  }, [parentEntity, status, batchId, timePeriod, limit, searchTerm]);

  // Pagination handlers
  const handleNextPage = () => {
    if (syncLogsData && Array.isArray(syncLogsData.logs) && syncLogsData.logs.length > 0) {
      const lastLog = syncLogsData.logs[syncLogsData.logs.length - 1];
      
      // Get timestamp with fallback to processedAt
      let lastSeenTimestamp = null;
      if (lastLog.timestamp) {
        lastSeenTimestamp = typeof lastLog.timestamp === 'string' 
          ? lastLog.timestamp 
          : new Date(lastLog.timestamp).toISOString();
      } else if (lastLog.processedAt) {
        lastSeenTimestamp = typeof lastLog.processedAt === 'string' 
          ? lastLog.processedAt 
          : new Date(lastLog.processedAt).toISOString();
      }
      
      // Key insight: The backend KQL query specifically uses:
      // customDimensions.mappingId for cursor-based pagination
      // So we need a reliable value for mappingId
      let lastSeenId = null;
      
      // Primary option: Use actual mappingId if available
      if (lastLog.mappingId) {
        lastSeenId = lastLog.mappingId;
      } 
      // Create a composite ID from batchId, entityId, and timestamp
      // This provides more uniqueness than just using one field
      else {
        const components = [];
        
        // Add batchId if available
        if (lastLog.batchId) {
          components.push(`batch_${lastLog.batchId}`);
        }
        
        // Add entityId if available
        if (lastLog.entityId) {
          components.push(`entity_${lastLog.entityId}`);
        }
        
        // Add entityName if available
        if (lastLog.entityName) {
          components.push(`ent_${lastLog.entityName}`);
        }
        
        // Add a timestamp component (always available)
        const timeComponent = lastSeenTimestamp 
          ? `t_${lastSeenTimestamp.replace(/[^0-9]/g, '')}`
          : `t_${Date.now()}`;
        components.push(timeComponent);
        
        // Create a composite ID with all available components
        // Ensuring it's properly sortable by having consistent components
        lastSeenId = components.join('_');
        
        // Ensure ID is unique by adding a random suffix if necessary
        if (components.length < 3) {
          lastSeenId += `_r${Math.random().toString(36).substring(2, 7)}`;
        }
      }
      
      // Ensure we have a timestamp for the cursor
      if (lastSeenTimestamp) {
        console.log("Setting cursor for next page:", { lastSeenTimestamp, lastSeenId });
        setCursorStack((prev) => [
          ...prev.slice(0, currentCursorIndex + 1),
          { lastSeenTimestamp, lastSeenId },
        ]);
        setCurrentCursorIndex((idx) => idx + 1);
      } else {
        console.error("Failed to extract timestamp from last log:", lastLog);
      }
    }
  };
  const handlePrevPage = () => {
    if (currentCursorIndex > 0) {
      setCurrentCursorIndex((idx) => idx - 1);
    }
  };

  // Defensive: Only allow valid entity names (not query strings)
  const handleParentEntityChange = (value: string) => {
    // Defensive: Only allow valid entity names (not query strings)
    if (typeof value === 'string' && !value.includes('=') && !value.includes('&')) {
      setParentEntity(value);
    } else if (value === 'all') {
      setParentEntity('all');
    }
  };

  return (
    <div className="container mx-auto py-6 px-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Field Sync Logs</h1>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isRefetching || syncLogsLoading}
        >
          {isRefetching || syncLogsLoading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {/* Filter Panel Toggle Button */}
      {!filterPanelOpen && (
        <div className="flex justify-end mb-2">
          <Button variant="outline" onClick={() => setFilterPanelOpen(true)}>
            Show Filters
          </Button>
        </div>
      )}

      {filterPanelOpen && (
        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Filter Logs</CardTitle>
            <Button size="icon" variant="ghost" aria-label="Close filters" onClick={() => setFilterPanelOpen(false)}>
              <X className="h-5 w-5" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div className="flex w-full max-w-md items-center space-x-2">
                  <Input
                    ref={searchInputRef}
                    placeholder="Search across all fields..."
                    className="h-10 w-full"
                    defaultValue={searchTerm}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                  />
                  <Button size="sm" variant="secondary" onClick={handleSearch}>
                    <Search className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSearch}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center space-x-2">
                  {searchTerm && (
                    <Badge variant="outline" className="text-xs bg-blue-50">
                      <span className="mr-1">Search:</span> {searchTerm}
                      <Button size="icon" variant="ghost" className="h-4 w-4 ml-1 p-0" onClick={clearSearch}>
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.resetColumnFilters()}
                  >
                    Reset Filters
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Parent Entity</label>
                  <Select value={parentEntity} onValueChange={handleParentEntityChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Parent Entity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Parent Entities</SelectItem>
                      {mappingsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading...
                        </SelectItem>
                      ) : (
                        uniqueParentEntities.map((entity: string) => (
                          <SelectItem key={entity} value={entity}>
                            {entity}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Time Period</label>
                  <Select value={timePeriod} onValueChange={setTimePeriod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Time Period" />
                    </SelectTrigger>
                    <SelectContent>
                      {timePeriodOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Batch ID</label>
                  <Select value={batchId} onValueChange={setBatchId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Batch ID" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Batch IDs</SelectItem>
                      {uniqueBatchIds.length === 0 ? (
                        <SelectItem value="loading" disabled>
                          No batches found
                        </SelectItem>
                      ) : (
                        uniqueBatchIds.map((id: string) => (
                          <SelectItem key={id} value={id}>
                            {id}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Display Mode</label>
                  <Select value={columnDisplayMode} onValueChange={setColumnDisplayMode}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select column display mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="priority">Priority (Important Fields)</SelectItem>
                      <SelectItem value="custom">Custom Selection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end space-x-2 h-full">
                  <Button variant="outline" onClick={clearFilters} className="w-full">
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Field Mapping Logs {syncLogsData?.totalCount !== undefined ? `(${syncLogsData.totalCount})` : ""}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
  

            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="font-medium text-sm">Columns:</span>
              {columnDisplayMode === "custom" && ALL_COLUMNS.map(col => (
                <label key={col.key} className="flex items-center gap-1 text-xs bg-gray-100 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(col.key)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedColumns([...selectedColumns, col.key]);
                      } else {
                        // Prevent removing last column
                        if (selectedColumns.length > 1) {
                          setSelectedColumns(selectedColumns.filter(k => k !== col.key));
                        }
                      }
                    }}
                    disabled={
                      selectedColumns.length === 1 && selectedColumns.includes(col.key)
                    }
                  />
                  {col.label}
                </label>
              ))}
              {columnDisplayMode !== "custom" && (
                <span className="text-xs text-gray-500">
                  Using {columnDisplayMode === "priority" ? "priority-based" : "auto-detected"} columns
                </span>
              )}
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {syncLogsLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        <div className="flex items-center justify-center">
                          <Loader2 className="h-8 w-8 animate-spin text-primary/70" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No logs found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="flex-1 text-sm text-muted-foreground">
                {syncLogsData?.logs.length} row(s) on this page of {syncLogsData?.totalCount || 0} total logs
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium">Rows per page</p>
                  <Select
                    value={limit.toString()}
                    onValueChange={(value) => setLimit(Number(value))}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue placeholder={limit.toString()} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {[10, 25, 50, 100].map((pageSize) => (
                        <SelectItem key={pageSize} value={pageSize.toString()}>
                          {pageSize}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={currentCursorIndex === 0}
                  >
                    <span className="sr-only">Go to previous page</span>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium">
                    Page {currentCursorIndex + 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!syncLogsData?.logs || syncLogsData.logs.length < limit}
                  >
                    <span className="sr-only">Go to next page</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log Details</DialogTitle>
            <DialogDescription>
              Details for log entry {selectedLog?.partitionKey}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4 max-w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="w-full max-w-full">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm">Basic Information</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 space-y-2">
                    <div className="grid grid-cols-[1fr,1fr] gap-1">
                      {/* Dynamically display all available properties */}
                      {Object.entries(selectedLog).map(([key, value]) => {
                        // Skip rendering syncLogs here as it has its own section
                        if (key === 'syncLogs') return null;
                        
                        // Format the value based on its type
                        let displayValue: React.ReactNode = "N/A";
                        
                        if (value !== null && value !== undefined) {
                          if (key === "status") {
                            const statusColor = 
                              value === "done"
                                ? "bg-green-100 text-green-800"
                                : value === "error"
                                  ? "bg-red-100 text-red-800"
                                  : value === "pending"
                                    ? "bg-blue-100 text-blue-800"
                                    : value === "no_update_needed"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-200 text-gray-800";
                            displayValue = <Badge className={statusColor}>{value}</Badge>;
                          } else if (key === "timestamp" || key === "processedAt") {
                            displayValue = value instanceof Date
                              ? format(value, "yyyy-MM-dd HH:mm:ss")
                              : typeof value === 'string'
                                ? format(new Date(value), "yyyy-MM-dd HH:mm:ss")
                                : String(value);
                          } else if (typeof value === "object") {
                            displayValue = <span className="text-xs">{JSON.stringify(value)}</span>;
                          } else {
                            displayValue = String(value);
                          }
                        }
                        
                        // Format the label
                        const formattedKey = key.replace(/([A-Z])/g, ' $1')
                          .replace(/^./, str => str.toUpperCase());
                        
                        return (
                          <React.Fragment key={key}>
                            <span className="text-sm font-medium">{formattedKey}:</span>
                            <span className="text-sm truncate" title={typeof displayValue === 'string' ? displayValue : ''}>
                              {displayValue}
                            </span>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sync Logs Section */}
              {selectedLog.syncLogs && (
                <Card className="w-full max-w-full">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm">Sync Logs</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="w-full overflow-hidden">
                      {(() => {
                        try {
                          // Handle when syncLogs is already an object (array)
                          if (typeof selectedLog.syncLogs === 'object') {
                            const logsArray = Array.isArray(selectedLog.syncLogs) 
                              ? selectedLog.syncLogs 
                              : [selectedLog.syncLogs];
                              
                            return (
                              <div className="text-xs bg-gray-100 p-2 rounded overflow-x-auto w-full">
                                {logsArray.map((item: any, index: number) => (
                                  <div key={index} className="mb-2 border-b pb-2">
                                    {Object.entries(item).map(([key, value], subIndex) => (
                                      <div key={`${index}-${subIndex}`} className="mb-1 ml-2">
                                        <span className="font-semibold text-blue-600">
                                          {key}:{" "}
                                        </span>
                                        <span className="whitespace-pre-wrap break-words">
                                          {typeof value === "object" && value !== null
                                            ? JSON.stringify(value, null, 2)
                                            : value === null ? "null" : String(value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          
                          // Try to parse as JSON if it's a string
                          const logContent = selectedLog.syncLogs;
                          
                          // Check if it looks like JSON
                          if (
                            typeof logContent === 'string' &&
                            (logContent.trim().startsWith("{") || logContent.trim().startsWith("["))
                          ) {
                            try {
                              const parsedJson = JSON.parse(logContent);
                              return (
                                <div className="text-xs bg-gray-100 p-2 rounded overflow-x-auto w-full">
                                  {Object.entries(parsedJson).map(
                                    ([key, value], index) => (
                                      <div key={index} className="mb-1">
                                        <span className="font-semibold text-blue-600">
                                          {key}:{" "}
                                        </span>
                                        <span className="whitespace-pre-wrap break-words">
                                          {typeof value === "object" && value !== null
                                            ? JSON.stringify(value, null, 2)
                                            : value === null ? "null" : String(value)}
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              );
                            } catch (e) {
                              // If JSON parsing fails, check for structured log format
                              if (logContent.includes("\n")) {
                                // Handle line-by-line logs with timestamps or key-value pairs
                                const lines = logContent
                                  .split("\n")
                                  .filter((line: string) => line.trim());
                                return (
                                  <div className="text-xs bg-gray-100 p-2 rounded overflow-x-auto w-full">
                                    {lines.map((line: string, index: number) => {
                                      // Try to identify if line has timestamp or key:value format
                                      const timestampMatch = line.match(
                                        /^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})/,
                                      );
                                      const keyValueMatch =
                                        line.match(/^([^:]+):(.*)/);

                                      if (timestampMatch) {
                                        const [fullMatch, timestamp] =
                                          timestampMatch;
                                        const message = line.substring(
                                          fullMatch.length,
                                        );
                                        return (
                                          <div key={index} className="mb-1">
                                            <span className="font-semibold text-blue-600">
                                              {timestamp}
                                            </span>
                                            <span className="whitespace-pre-wrap break-words">
                                              {message}
                                            </span>
                                          </div>
                                        );
                                      } else if (keyValueMatch) {
                                        const key = keyValueMatch[1];
                                        const value = keyValueMatch[2];
                                        return (
                                          <div key={index} className="mb-1">
                                            <span className="font-semibold text-blue-600">
                                              {key}
                                            </span>
                                            <span className="whitespace-pre-wrap break-words">
                                              {value}
                                            </span>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                  </div>
                                );
                              }
                            }
                          }

                          // Fallback: plain text display
                          return (
                            <div className="whitespace-pre-wrap break-words">
                              {String(selectedLog.syncLogs)}
                            </div>
                          );
                        } catch (e) {
                          return (
                            <div className="text-red-500 text-sm">
                              Error rendering sync logs.
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldSyncLogs;
